import { useEffect, useRef } from "react";
import "./atalhos.css";
import {
  acaoDoHub,
  ehAcionavelPeloNavegador,
  ehCampoDeTexto,
  ehTeclaDeAcionamento,
  normalizarTecla,
  type AcaoDeAtalho,
} from "./atalhos";

/**
 * Escuta do teclado, em um ouvinte só no documento.
 *
 * Três regras mandam neste arquivo, e nenhuma delas pode falhar:
 *
 * 1. Atalho não dispara com o foco em campo de digitação. Quem escreve "Neto"
 *    na busca não joga a rodada por causa do "N".
 * 2. Com um diálogo aberto, o resto do jogo fica mudo. Só as teclas que aquele
 *    diálogo registrou valem, porque é nele que o jogador está.
 * 3. Esc fecha o diálogo do topo, e topo é o que está por cima na tela, não o
 *    último que abriu. Resumo da rodada e fim de temporada podem estar abertos
 *    ao mesmo tempo, e quem cobre a tela é o de maior nível.
 */

/** Um diálogo aberto e as teclas que ele mesmo trata. */
export interface DialogoDeAtalhos {
  /** Só para depuração e leitura do código. */
  nome: string;
  /** O mesmo `zIndex` do diálogo na tela, que define quem está por cima. */
  nivel: number;
  /** O que fazer no Esc. Sem isto, o diálogo não tem saída pelo teclado. */
  aoFechar?: (() => void) | null;
  /** Teclas normalizadas que este diálogo trata enquanto está aberto. */
  atalhos?: Partial<Record<string, () => void>>;
}

interface Registro {
  ordem: number;
  dados: { current: DialogoDeAtalhos };
}

const abertos: Registro[] = [];
let contador = 0;

/** O diálogo que está por cima de todos, ou nulo quando não há nenhum. */
export function dialogoDoTopo(): DialogoDeAtalhos | null {
  let melhor: Registro | null = null;
  for (const registro of abertos) {
    if (!melhor) {
      melhor = registro;
      continue;
    }
    const nivel = registro.dados.current.nivel;
    const nivelAtual = melhor.dados.current.nivel;
    if (nivel > nivelAtual || (nivel === nivelAtual && registro.ordem > melhor.ordem)) {
      melhor = registro;
    }
  }
  return melhor ? melhor.dados.current : null;
}

/** Há algum diálogo aberto. */
export function temDialogoAberto(): boolean {
  return abertos.length > 0;
}

/**
 * Registra o diálogo enquanto ele estiver montado.
 *
 * O registro entra na pilha uma vez, mas as funções de dentro dele são lidas na
 * hora da tecla: assim o diálogo pode trocar de botão a cada render sem se
 * registrar de novo.
 */
export function useDialogoDeAtalhos(dados: DialogoDeAtalhos): void {
  const atual = useRef(dados);

  useEffect(() => {
    atual.current = dados;
  });

  useEffect(() => {
    contador += 1;
    const registro: Registro = { ordem: contador, dados: atual };
    abertos.push(registro);
    return () => {
      const indice = abertos.indexOf(registro);
      if (indice >= 0) abertos.splice(indice, 1);
    };
  }, []);
}

/**
 * Gancho do hub: escuta o documento e devolve a ação pedida pela tecla.
 *
 * Fica montado uma vez só, na tela que sabe executar as ações. Os diálogos não
 * montam ouvinte próprio: eles se registram e este mesmo ouvinte os atende.
 */
export function useAtalhosDeTeclado(
  aoDisparar: (acao: AcaoDeAtalho, tecla: string) => void,
): void {
  const executar = useRef(aoDisparar);

  useEffect(() => {
    executar.current = aoDisparar;
  });

  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent): void {
      // Combinação com Ctrl, Alt ou Meta é do navegador ou do sistema.
      if (evento.ctrlKey || evento.altKey || evento.metaKey) return;
      if (evento.defaultPrevented || evento.repeat) return;

      const tecla = normalizarTecla(evento);

      if (ehCampoDeTexto(evento.target)) {
        /*
         * Nenhum atalho vale enquanto o jogador digita, nem o Esc: fechar o
         * painel embaixo de quem está escrevendo o valor de uma proposta seria
         * perder o que ele escreveu. O Esc aqui só devolve o teclado ao jogo,
         * saindo do campo. O Esc seguinte já fecha o painel, porque o foco
         * deixou de estar no campo.
         */
        if (tecla === "esc" && evento.target instanceof HTMLElement) evento.target.blur();
        return;
      }

      // Enter e espaço com um botão em foco pertencem ao botão.
      if (ehTeclaDeAcionamento(tecla) && ehAcionavelPeloNavegador(evento.target)) return;

      const dialogo = dialogoDoTopo();
      if (dialogo) {
        if (tecla === "esc") {
          if (!dialogo.aoFechar) return;
          evento.preventDefault();
          dialogo.aoFechar();
          return;
        }
        const proprio = dialogo.atalhos?.[tecla];
        if (!proprio) return;
        evento.preventDefault();
        proprio();
        return;
      }

      const acao = acaoDoHub(tecla);
      if (!acao) return;
      evento.preventDefault();
      // A tecla vai junto porque um mesmo atalho pode servir a vários assentos.
      executar.current(acao, tecla);
    }

    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, []);
}
